// What the signing sheet says when the server will not hand out a link.
//
// The sheet used to print the raw failure — `API 410: {"error":"gone",
// "reason":"approval_superseded"}` — to an operator who had asked for a second
// link to a card he had just signed (review of 4 October, R19). The server now
// says "signed" when a card is signed, and this turns each reason it can give
// into a sentence. An unknown reason is shown as it came: a refusal nobody
// planned for must not be softened into a guess.

const SENTENCES: Record<string, string> = {
  approval_signed:
    'Already signed. The signature is recorded and the desk sends it to the network — nothing more to do here.',
  already_approved:
    'Already approved. This address is payable — nothing more to do here.',
  approval_expired:
    'The signing window for this transaction has closed, and nothing was sent. A fresh one appears in the list.',
  approval_superseded:
    'This transaction was replaced by a newer build. Open the newer one from the list.',
  no_registered_device:
    'No signing device is registered for you, so a link would be no use. Ask for your Ledger address to be registered.',
  self_approval_refused:
    'You proposed this one, so you cannot approve it. It needs a second person.',
  kyt_flagged:
    'This address was screened as flagged. It cannot be approved.',
};

/** True when the refusal means the work is already done, not that something is wrong. */
export function refusalIsGoodNews(message: string): boolean {
  const reason = reasonIn(message);
  return reason === 'approval_signed' || reason === 'already_approved';
}

/**
 * The reason, wherever the message carries it. The raw body says
 * `"reason":"self_approval_refused"`; main's ApiError (lib/errors.ts) rewrites a
 * 403 as `You are not allowed to do this: self_approval_refused.` before it gets
 * here — and the operator saw exactly that code on screen (16 Oct 2026).
 */
function reasonIn(message: string): string | null {
  const quoted = /"reason"\s*:\s*"([^"]+)"/.exec(message)?.[1];
  if (quoted) return quoted;
  return Object.keys(SENTENCES).find((key) => new RegExp(`\\b${key}\\b`).test(message)) ?? null;
}

export function linkRefusal(message: string): string {
  const reason = reasonIn(message);
  return (reason !== null && Object.hasOwn(SENTENCES, reason)) ? SENTENCES[reason]! : message;
}
